import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef, HostListener, Input, Output, EventEmitter, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent } from '../primeng';
import { FilterPanelComponent, FilterField } from '../filter-panel/filter-panel.component';
import { ActionDropdownTriggerDirective } from '../../directives/action-dropdown-trigger.directive';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { ProviderPreviewMetadataComponent } from '../contribution-steps/preview-metadata/preview-metadata.component';
import { CatalogueApiService } from '@angular-workspace/services';
import { AssetTypeMapperService } from '@angular-workspace/core';
import { ToasterService } from '../../../services/toaster.service';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';

export interface ContributionData {
  assetName: string;
  assetType: string;
  permission: string;
  status: string;
  visibility: string; // 'Visible' | 'Hidden' from dataUploadStatus
  verified: string | boolean;
  publishedOn: string;
  action?: string;
  originalData?: any; // Store original API response data
}

export interface ContributionFilters {
  search: string;
  assetType: string[];
  status: string[];
  dateRange: { start: Date | null; end: Date | null } | null;
}

@Component({
  selector: 'app-manage-contribution',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TableComponent,
    FilterPanelComponent,
    ConfirmationDialogComponent,
    ModalComponent,
    ProviderPreviewMetadataComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './manage-contribution.component.html',
  styleUrl: './manage-contribution.component.scss',
})
export class ManageContributionComponent implements OnInit, AfterViewInit, OnDestroy {




















































































   
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('permissionCell', { static: false }) permissionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;
  /** When 'provider', action dropdown shows View, Modify Metadata, Delete Asset. When 'platform-admin' (default), shows View, Hide/Unhide, Delete. */
  @Input() actionContext: 'provider' | 'platform-admin' = 'platform-admin';

  @Output() reloadData = new EventEmitter<void>();
  @Output() filterChange = new EventEmitter<any[]>();
  @Output() sortChange = new EventEmitter<{ column: string; direction: 'asc' | 'desc' }>();
  /** Emitted when provider context needs data for a given page/size/sort (parent should call myassets API). */
  @Output() requestData = new EventEmitter<{ page: number; pageSize: number; sort: string }>();

  private searchSubject = new Subject<Record<string, any>>();
  private searchSubscription: any;
  private latestFilters: Record<string, any> = {};

  constructor(
    private catalogueApiService: CatalogueApiService,
    private router: Router,
    private toasterService: ToasterService,
    private assetTypeMapper: AssetTypeMapperService,
    private cdr: ChangeDetectorRef
  ) {
    // Setup debounced search
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged((prev, curr) => {
        // Only compare search text to avoid unnecessary processing
        return prev && curr && prev['search'] === curr['search'];
      })
    ).subscribe(filters => {
      // Just process filters; do not mutate filterFields for search, to avoid recreating the input and losing focus
      this.processFilterChangeWithFilters(filters);
    });
  }

  openDropdownId: string | null = null;
  dropdownPosition: { [key: string]: 'bottom' | 'top' } = {};
  /** Position for fixed overlay (dropdown above table). */
  overlayPosition: { top: number; left: number; width: number } | null = null;

  // Filter fields configuration
  filterFields: FilterField[] = [
    
    {
      key: 'assetType',
      label: 'Asset Type',
      type: 'multiselect',
      options: ['Dataset', 'Model', 'Usecase'],
      value: []
    },
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search assets',
      value: ''
    }
  ];

  columns: TableColumn[] = [
    {
      key: 'assetName',
      label: 'Asset Name',
      sortable: true,
      width: '20%'
    },
    {
      key: 'assetType',
      label: 'Asset Type',
      sortable: false,
      width: '12%'
    },
    {
      key: 'permission',
      label: 'Permission',
      sortable: true,
      width: '12%'
    },
    {
      key: 'status',
      label: 'Publish Status',
      sortable: true,
      width: '12%'
    },
    {
      key: 'publishedOn',
      label: 'Published On',
      sortable: true,
      width: '12%',
      align: 'left'
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '10%',
      align: 'left',
      fixed: 'right'
    }
  ];

  // Contributions data for current page - will be populated from API
  contributionsData: ContributionData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true // Enable server-side pagination
  };

  loading = false;
  emptyMessage = 'No contributions found';

  /** Current sort state for server-side sorting */
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  /** Delete confirmation modal */
  showDeleteConfirmModal = false;
  rowToDelete: ContributionData | null = null;
  isDeleting = false;

  /** Modify metadata modal (provider context) */
  showModifyModal = false;
  currentModifyAsset: ContributionData | null = null;
  modifyAssetData: any = null;
  isLoadingAssetData = false;

  ngOnInit(): void {
    // Provider context: only Dataset and Model in asset type filter (no Usecase)
    if (this.actionContext === 'provider') {
      const assetTypeField = this.filterFields.find(f => f.key === 'assetType');
      if (assetTypeField) {
        assetTypeField.options = ['Dataset', 'Model'];
      }
    }
    // Data will be loaded via getMyAssets() from parent component
  }

  /**
   * Map and set contributions data from getMyAssets API response
   * @param response The response from getMyAssets API
   */
  mapMyAssetsResponse(response: any): void {
    console.log('mapMyAssetsResponse called with:', response);
    this.closeActionDropdown(); // Reset dropdown state after data reload (e.g. post-delete)
    this.loading = true;
    try {
      let mappedData: ContributionData[] = [];

      // Handle API response structure: { result: [...], paginationInfo: {...} }
      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        mappedData = dataArray.map((item: any) => {
          const dateValue = item.itemCreatedAt || item.createdAt || item.publishDate || '';
          // Publish Status from response: ACTIVE → Granted, PENDING → Pending, DECLINED → Rejected
          const publishStatus = this.mapPublishStatusFromResponse(
            item.publishStatus ?? item.itemStatus ?? item.status
          );

          return {
            assetName: item.label || item.name || 'N/A',
            assetType: this.formatAssetType(item.type),
            permission: item.accessPolicy || 'N/A',
            status: publishStatus,
            visibility: (publishStatus === 'Pending' || publishStatus === 'Rejected') ? 'N/A' : (item.dataUploadStatus === false ? 'Hidden' : 'Visible'),
            verified: item.verified !== undefined ? (item.verified ? 'Yes' : 'No') : 'N/A',
            publishedOn: dateValue ? this.formatDate(dateValue) : 'N/A',
            action: 'Action',
            originalData: item // Store original API response data
          };
        });

        // Update pagination from paginationInfo
        // API returns one page at a time - use server-side pagination so table displays data as-is
        const totalCount = response['paginationInfo']?.totalCount ?? mappedData.length;
        const currentPage = response['paginationInfo']?.page ?? this.pagination.currentPage;
        this.pagination = {
          ...this.pagination,
          currentPage,
          totalItems: totalCount,
          serverSide: true // Server-side: API returns one page, don't slice
        };
      } else if (response && response['paginationInfo']) {
        // Handle case where response has paginationInfo but no result array
        const totalCount = response['paginationInfo']['totalCount'] ?? 0;
        const currentPage = response['paginationInfo']['page'] ?? this.pagination.currentPage;
        this.pagination = {
          ...this.pagination,
          currentPage,
          totalItems: totalCount,
          serverSide: true
        };
        mappedData = []; // No data to map
      } else if (Array.isArray(response)) {
        // Fallback: Handle direct array response
        mappedData = response.map((item: any) => {
          const dateValue = item.itemCreatedAt || item.createdAt || item.publishDate || '';
          const publishStatus = this.mapPublishStatusFromResponse(
            item.publishStatus ?? item.itemStatus ?? item.status
          );

          return {
            assetName: item.label || item.name || 'N/A',
            assetType: this.formatAssetType(item.type),
            permission: item.accessPolicy || 'N/A',
            status: publishStatus,
            visibility: (publishStatus === 'Pending' || publishStatus === 'Rejected') ? 'N/A' : (item.dataUploadStatus === false ? 'Hidden' : 'Visible'),
            verified: item.verified !== undefined ? (item.verified ? 'Yes' : 'No') : 'N/A',
            publishedOn: dateValue ? this.formatDate(dateValue) : 'N/A',
            action: 'Action',
            originalData: item
          };
        });
        this.pagination = {
          ...this.pagination,
          totalItems: mappedData.length,
          serverSide: true
        };
      } else {
        // Handle unexpected response structure
        console.warn('mapMyAssetsResponse: Unexpected response structure:', response);
        mappedData = [];
        this.pagination = {
          ...this.pagination,
          totalItems: 0,
          serverSide: true
        };
      }

      this.contributionsData = mappedData;
      console.log('mapMyAssetsResponse: Data mapped - count:', mappedData.length, 'totalItems:', this.pagination.totalItems);
      
      if (mappedData.length === 0) {
        this.emptyMessage = 'No contributions found';
      }
    } catch (error) {
      console.error('Error mapping my assets response:', error);
      this.contributionsData = [];
      this.pagination = {
        ...this.pagination,
        totalItems: 0,
        serverSide: true
      };
      this.emptyMessage = 'No contributions found';
    } finally {
      this.loading = false;
      // Force change detection after data reload so Action dropdown works correctly post-delete
      setTimeout(() => this.cdr.detectChanges(), 0);
    }
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    const actionColumn = this.columns.find(col => col.key === 'action');
    if (actionColumn && this.actionCellTemplate) {
      actionColumn.cellTemplate = this.actionCellTemplate;
    }
    
    const permissionColumn = this.columns.find(col => col.key === 'permission');
    if (permissionColumn && this.permissionCellTemplate) {
      permissionColumn.cellTemplate = this.permissionCellTemplate;
    }
    
    const statusColumn = this.columns.find(col => col.key === 'status');
    if (statusColumn && this.statusCellTemplate) {
      statusColumn.cellTemplate = this.statusCellTemplate;
    }
  }

  async loadContributions(): Promise<void> {
    console.log('loadContributions: START - setting loading = true');
    console.trace('loadContributions: Called from:');
    this.loading = true;
    try {
      // Get current filter values from filterFields
      const filters: Record<string, any> = {};
      this.filterFields.forEach(field => {
        filters[field.key] = field.value;
      });

      // Transform dateRange from { startDate, endDate } to { start, end } format
      let dateRange = null;
      if (filters['dateRange'] && filters['dateRange'].startDate && filters['dateRange'].endDate) {
        dateRange = {
          start: filters['dateRange'].startDate,
          end: filters['dateRange'].endDate
        };
      }

      const response = await this.catalogueApiService.getAllAssets(
        this.pagination.currentPage,
        this.pagination.pageSize,
        filters['search'] || '',
        filters['assetType'] || [],
        filters['status'] || [],
        dateRange,
        this.getSortString()
      );

      let mappedData: ContributionData[] = [];

      // Handle API response structure: { result: [...], paginationInfo: {...} }
      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        mappedData = dataArray.map((item: any) => {
          const dateValue = item.itemCreatedAt || item.createdAt || item.publishDate || '';
          const publishStatus = this.mapPublishStatusFromResponse(
            item.publishStatus ?? item.itemStatus ?? item.status
          );

          return {
            assetName: item.label || item.name || 'N/A',
            assetType: this.formatAssetType(item.type),
            permission: item.accessPolicy || 'N/A',
            status: publishStatus,
            visibility: (publishStatus === 'Pending' || publishStatus === 'Rejected') ? 'N/A' : (item.dataUploadStatus === false ? 'Hidden' : 'Visible'),
            verified: item.verified !== undefined ? (item.verified ? 'Yes' : 'No') : 'N/A',
            publishedOn: dateValue ? this.formatDate(dateValue) : 'N/A',
            action: 'Action',
            originalData: item // Store original API response data
          };
        });

        // Update pagination from paginationInfo - keep serverSide: true
        if (response['paginationInfo'] && response['paginationInfo']['totalCount'] !== undefined) {
          const currentPage = response['paginationInfo']['page'] ?? this.pagination.currentPage;
          this.pagination = {
            ...this.pagination,
            currentPage,
            totalItems: response['paginationInfo']['totalCount'],
            serverSide: true
          };
        }
      } else if (response && response['paginationInfo']) {
        // Handle case where response has paginationInfo but no result array
        if (response['paginationInfo']['totalCount'] !== undefined) {
          const currentPage = response['paginationInfo']['page'] ?? this.pagination.currentPage;
          this.pagination = {
            ...this.pagination,
            currentPage,
            totalItems: response['paginationInfo']['totalCount'],
            serverSide: true
          };
        }
        mappedData = []; // No data to map
      } else if (Array.isArray(response)) {
        // Fallback: Handle direct array response
        mappedData = response.map((item: any) => {
          const dateValue = item.itemCreatedAt || item.createdAt || item.publishDate || '';
          const publishStatus = this.mapPublishStatusFromResponse(
            item.publishStatus ?? item.itemStatus ?? item.status
          );
          return {
            assetName: item.label || item.name || 'N/A',
            assetType: this.formatAssetType(item.type),
            permission: item.accessPolicy || 'N/A',
            status: publishStatus,
            visibility: (publishStatus === 'Pending' || publishStatus === 'Rejected') ? 'N/A' : (item.dataUploadStatus === false ? 'Hidden' : 'Visible'),
            verified: item.verified !== undefined ? (item.verified ? 'Yes' : 'No') : 'N/A',
            publishedOn: dateValue ? this.formatDate(dateValue) : 'N/A',
            action: 'Action',
            originalData: item
          };
        });
        this.pagination = {
          ...this.pagination,
          totalItems: mappedData.length,
          serverSide: true
        };
      }

      this.contributionsData = mappedData;
      console.log('loadContributions: Data assigned - count:', mappedData.length, 'loading:', this.loading);
      
      if (mappedData.length === 0) {
        this.emptyMessage = 'No contributions found';
      }
    } catch (error) {
      console.error('Error loading contributions:', error);
      this.contributionsData = [];
      this.pagination = {
        ...this.pagination,
        totalItems: 0
      };
      this.emptyMessage = 'No contributions found';
    } finally {
      console.log('loadContributions: FINALLY - setting loading = false');
      this.loading = false;
      console.log('loadContributions: COMPLETE - loading is now:', this.loading, 'data count:', this.contributionsData.length);
    }
  }

  formatAssetType(typeArray: string[] | string | undefined): string {
    if (!typeArray) return 'N/A';
    
    // Handle array of types
    if (Array.isArray(typeArray) && typeArray.length > 0) {
      const type = typeArray[0];
      // Extract the type name after the colon (e.g., "adex:Apps" -> "Apps")
      const parts = type.split(':');
      let typeName = parts.length > 1 ? parts[parts.length - 1] : type;
      
      // Normalize case for comparison
      const normalizedTypeName = typeName.toLowerCase();
      
      // Format common types (case-insensitive matching)
      if (normalizedTypeName === 'aimodel' || normalizedTypeName === 'ai_model' || normalizedTypeName === 'ai model') {
        return 'Model';
      } else if (normalizedTypeName === 'apps' || normalizedTypeName === 'app') {
        return 'Usecase';
      } else if (normalizedTypeName === 'databank' || normalizedTypeName === 'data_bank') {
        return 'Dataset';
      } else if (normalizedTypeName === 'usecase' || normalizedTypeName === 'use_case' || normalizedTypeName === 'use case') {
        return 'Usecase';
      } else if (normalizedTypeName === 'challenge') {
        return 'Challenge';
      }
      
      // Fallback to original typeName if no match
      return typeName;
    }
    
    // Handle string type
    if (typeof typeArray === 'string') {
      const parts = typeArray.split(':');
      let typeName = parts.length > 1 ? parts[parts.length - 1] : typeArray;
      
      // Normalize case for comparison
      const normalizedTypeName = typeName.toLowerCase();
      
      // Format common types (case-insensitive matching)
      if (normalizedTypeName === 'aimodel' || normalizedTypeName === 'ai_model' || normalizedTypeName === 'ai model') {
        return 'Model';
      } else if (normalizedTypeName === 'apps' || normalizedTypeName === 'app') {
        return 'Usecase';
      } else if (normalizedTypeName === 'databank' || normalizedTypeName === 'data_bank') {
        return 'Dataset';
      } else if (normalizedTypeName === 'usecase' || normalizedTypeName === 'use_case' || normalizedTypeName === 'use case') {
        return 'Usecase';
      }
      
      return typeName;
    }
    
    return 'N/A';
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  }

  onPageChange(page: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: page
    };
    if (this.actionContext === 'provider') {
      this.requestData.emit({
        page: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
        sort: this.getSortString()
      });
      return;
    }
    this.loadContributions();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: pageSize,
      currentPage: 1
    };
    if (this.actionContext === 'provider') {
      this.requestData.emit({
        page: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
        sort: this.getSortString()
      });
      return;
    }
    this.loadContributions();
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.pagination = { ...this.pagination, currentPage: 1 };
    if (this.actionContext === 'provider') {
      this.requestData.emit({
        page: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
        sort: this.getSortString()
      });
      this.sortChange.emit(event);
      return;
    }
    this.sortChange.emit(event);
  }

  /** Map column key to API sort field and return sort string (e.g. "label:asc") */
  getSortString(): string {
    if (!this.sortColumn) return 'itemCreatedAt:desc';
    const fieldMap: Record<string, string> = {
      assetName: 'name',
      permission: 'accessPolicy',
      status: 'publishStatus',
      publishedOn: 'itemCreatedAt'
    };
    const apiField = fieldMap[this.sortColumn] || 'itemCreatedAt';
    return `${apiField}:${this.sortDirection}`;
  }

  onFilterChange(filters: Record<string, any>): void {
    // Store latest filters for debounced search
    this.latestFilters = { ...filters };
    
    // Store the current search text
    const searchText = filters['search'] || '';
    const previousSearchText = this.filterFields.find(f => f.key === 'search')?.value || '';
    const isSearchChange = searchText !== previousSearchText;
    
    // Update filterFields for non-search fields only (to avoid input focus loss)
    // Use direct assignment instead of map to avoid creating new array reference
    // The filter panel component manages its own state via filterValues, so we don't need to
    // update filterFields for search during typing - it will maintain its value automatically
    this.filterFields.forEach(field => {
      if (field.key !== 'search' && filters[field.key] !== undefined) {
        // Update other fields immediately using direct assignment
        field.value = filters[field.key];
      }
    });

    // If search text changed, debounce it with all current filter values
    if (isSearchChange) {
      this.searchSubject.next(this.latestFilters);
    } else {
      // For non-search changes, process immediately with current filters
      this.processFilterChangeWithFilters(filters);
    }
  }

  private processFilterChange(): void {
    // Get current filter values from filterFields
    const filters: Record<string, any> = {};
    this.filterFields.forEach(field => {
      filters[field.key] = field.value;
    });
    this.processFilterChangeWithFilters(filters);
  }

  private processFilterChangeWithFilters(filters: Record<string, any>): void {

    // Build searchCriteria array for type filter.
    // By default, limit to Dataset / Model / Apps types so Manage Assets only searches these.
    const defaultTypeValues = ['adex:DataBank', 'adex:AiModel', 'adex:Apps'];
    const searchCriteria: any[] = [
      {
        searchType: 'term',
        field: 'type',
        values: [...defaultTypeValues],
      },
    ];
    
    // If Asset Type filter is selected, override the default values with the selected types.
    if (filters['assetType'] && Array.isArray(filters['assetType']) && filters['assetType'].length > 0) {
      // Map display types to API format (Dataset -> adex:DataBank, Model -> adex:AiModel, Usecase -> adex:UseCase)
      const apiAssetTypes = filters['assetType'].map((type: string) => 
        this.assetTypeMapper.mapAssetTypeToApiFormat(type)
      );
      searchCriteria[0].values = apiAssetTypes;
    }
    
    // Build payload based on whether search text is provided
    const searchText = filters['search'] || '';
    let filterPayload: any;
    
    if (searchText && searchText.trim() !== '') {
      // If search text is provided, use the full payload format with q, autoComplete, fuzzy, and searchCriteria
      filterPayload = {
        q: searchText.trim(),
        autoComplete: true,
        fuzzy: true,
        searchCriteria: searchCriteria
      };
    } else if (searchCriteria.length > 0) {
      // If no search text but assetType filter exists, wrap searchCriteria in an object
      filterPayload = {
        searchCriteria: searchCriteria
      };
    } else {
      // No filters, send empty array
      filterPayload = [];
    }
    
    // Emit filter payload to parent component
    this.filterChange.emit(filterPayload);

    // Reset to page 1
    this.pagination = {
      ...this.pagination,
      currentPage: 1
    };
    
    // Note: Data reloading is handled by parent component via filterChange event
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  onClearFilters(): void {
    // Reset all filter fields
    this.filterFields = this.filterFields.map(field => ({
      ...field,
      value: field.type === 'multiselect' ? [] : field.type === 'dateRange' ? null : ''
    }));

    // Emit empty array to parent component (no filters)
    this.filterChange.emit([]);

    // Reset to page 1
    this.pagination = {
      ...this.pagination,
      currentPage: 1
    };
    
    // Note: Data reloading is handled by parent component via filterChange event
  }

  toggleActionDropdown(event: Event, row: ContributionData): void {
    event.stopPropagation();
    event.preventDefault();
    const rowId = row.originalData?.id || JSON.stringify(row);
    if (this.openDropdownId === rowId) {
      this.openDropdownId = null;
      this.overlayPosition = null;
      delete this.dropdownPosition[rowId];
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.overlayPosition = { top: rect.bottom + 4, left: rect.left, width: rect.width };
    this.openDropdownId = rowId;
    this.setDropdownPosition(event, rowId);
    // CD run by appActionDropdownTrigger directive
  }

  setDropdownPosition(event: Event, rowId: string): void {
    const button = event.target as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 150; // Approximate dropdown height (3 items + padding)
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    
    // If there's not enough space below but enough space above, open upward
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      this.dropdownPosition[rowId] = 'top';
    } else {
      this.dropdownPosition[rowId] = 'bottom';
    }
  }

  getDropdownPosition(row: ContributionData): 'bottom' | 'top' {
    const rowId = row.originalData?.id || JSON.stringify(row);
    return this.dropdownPosition[rowId] || 'bottom';
  }

  closeActionDropdown(): void {
    if (this.openDropdownId) {
      delete this.dropdownPosition[this.openDropdownId];
    }
    this.openDropdownId = null;
    this.overlayPosition = null;
  }

  /** Row whose action dropdown is open; used by the fixed overlay. */
  get openDropdownRow(): ContributionData | null {
    if (this.openDropdownId == null) return null;
    return this.contributionsData.find(
      (r) => (r.originalData?.id ?? JSON.stringify(r)) === this.openDropdownId
    ) ?? null;
  }

  isDropdownOpen(row: ContributionData): boolean {
    const rowId = row.originalData?.id || JSON.stringify(row);
    return this.openDropdownId === rowId;
  }

  getPermissionBadgeClass(permission: string): string {
    if (!permission) return 'permission-badge';
    const permissionUpper = permission.toUpperCase();
    // Handle both PUBLIC/Public and RESTRICTED/Restricted and PRIVATE
    if (permissionUpper === 'PUBLIC' || permissionUpper === 'OPEN') {
      return 'permission-badge permission-badge-public';
    } else if (permissionUpper === 'RESTRICTED') {
      return 'permission-badge permission-badge-restricted';
    } else if (permissionUpper === 'PRIVATE') {
      return 'permission-badge permission-badge-private';
    }
    return 'permission-badge';
  }

  getPermissionLabel(permission: string): string {
    if (!permission) return 'N/A';
    const permissionUpper = permission.toUpperCase();
    // Map API values to display labels
    if (permissionUpper === 'OPEN') {
      return 'Public';
    }
    // Return capitalized first letter + rest lowercase
    return permission.charAt(0).toUpperCase() + permission.slice(1).toLowerCase();
  }

  /**
   * Map API publish status to display label.
   * ACTIVE → Granted, PENDING → Pending, DECLINED → Rejected.
   */
  mapPublishStatusFromResponse(raw: string | undefined): string {
    if (raw == null || String(raw).trim() === '') return 'N/A';
    const s = String(raw).toUpperCase().trim();
    if (s === 'ACTIVE') return 'Granted';
    if (s === 'PENDING') return 'Pending';
    if (s === 'DECLINED') return 'Rejected';
    return raw;
  }

  /** Returns circle indicator class: green for Visible (Granted), red for Hidden (Pending), gray for Rejected */
  getStatusIndicatorClass(status: string): string {
    if (!status) return 'status-indicator status-indicator-default';
    const statusUpper = status.toUpperCase();
    if (statusUpper === 'GRANTED') return 'status-indicator status-indicator-visible';
    if (statusUpper === 'PENDING') return 'status-indicator status-indicator-hidden';
    if (statusUpper === 'REJECTED') return 'status-indicator status-indicator-rejected';
    return 'status-indicator status-indicator-default';
  }

  getStatusLabel(status: string): string {
    if (!status) return 'N/A';
    return status;
  }

  /** Display label for Publish Status: Granted → Visible, Pending → Hidden, Rejected → Rejected */
  getPublishStatusDisplayLabel(status: string): string {
    if (!status) return 'N/A';
    const s = status.toUpperCase();
    if (s === 'GRANTED') return 'Visible';
    if (s === 'PENDING') return 'Hidden';
    if (s === 'REJECTED') return 'Rejected';
    return status;
  }

  /** Returns chip variant class for Publish Status (pill/chip design). */
  getStatusChipClass(status: string): string {
    if (!status) return 'publish-status-chip publish-status-chip--default';
    const s = status.toUpperCase();
    if (s === 'GRANTED') return 'publish-status-chip publish-status-chip--granted';
    if (s === 'PENDING') return 'publish-status-chip publish-status-chip--pending';
    if (s === 'REJECTED') return 'publish-status-chip publish-status-chip--rejected';
    return 'publish-status-chip publish-status-chip--default';
  }

  /** Returns dot indicator class for Visibility column: green for Visible, red for Hidden, neutral for N/A. */
  getVisibilityIndicatorClass(visibility: string): string {
    if (!visibility || visibility === 'N/A') return 'visibility-indicator visibility-indicator-na';
    return visibility === 'Visible'
      ? 'visibility-indicator visibility-indicator-visible'
      : 'visibility-indicator visibility-indicator-hidden';
  }

  /**
   * Get the route path based on asset type
   * @param type The asset type from API (e.g., ["adex:Apps"], ["adex:DataBank"], ["adex:AiModel"])
   * @returns The route path (e.g., 'usecase', 'dataset', 'model')
   */
  getRoutePathFromType(type: string[] | string | undefined): string {
    if (!type) return 'dataset'; // Default to dataset
    
    // Handle array of types
    let typeString = '';
    if (Array.isArray(type) && type.length > 0) {
      typeString = type[0];
    } else if (typeof type === 'string') {
      typeString = type;
    } else {
      return 'dataset'; // Default fallback
    }
    
    // Extract the type name after the colon (e.g., "adex:Apps" -> "Apps")
    const parts = typeString.split(':');
    const typeName = parts.length > 1 ? parts[parts.length - 1] : typeString;
    const normalizedTypeName = typeName.toLowerCase();
    
    // Map to route paths
    if (normalizedTypeName === 'aimodel' || normalizedTypeName === 'ai_model' || normalizedTypeName === 'ai model') {
      return 'model';
    } else if (normalizedTypeName === 'apps' || normalizedTypeName === 'app') {
      return 'usecase';
    } else if (normalizedTypeName === 'databank' || normalizedTypeName === 'data_bank') {
      return 'dataset';
    } else if (normalizedTypeName === 'usecase' || normalizedTypeName === 'use_case' || normalizedTypeName === 'use case') {
      return 'usecase';
    }
    
    return 'dataset'; // Default fallback
  }

  async handleAction(action: string, row: ContributionData): Promise<void> {
    this.closeActionDropdown();
    console.log(`${action} action for:`, row);
    
    if (action === 'view') {
      // Navigate to the appropriate detail page based on asset type
      const assetId = row.originalData?.id;
      if (assetId) {
        const assetType = row.originalData?.type;
        const routePath = this.getRoutePathFromType(assetType);
        const urlTree = this.router.createUrlTree([`/${routePath}`, assetId]);
        const url = this.router.serializeUrl(urlTree);
        window.open(url, '_blank');
      } else {
        console.error('Asset ID not found for view action');
      }
    } else if (action === 'modifyMetadata') {
      this.openModifyMetadataModal(row);
    } else if (action === 'hide' || action === 'unhide') {
      // Hide/Unhide asset - call hideOrUnhideItems endpoint
      const assetId = row.originalData?.id;
      if (!assetId) {
        console.error('Asset ID not found');
        return;
      }

      const isHiding = action === 'hide';
      console.log(`${isHiding ? 'Hide' : 'Unhide'}: Starting ${isHiding ? 'hide' : 'unhide'} for asset ID:`, assetId);
      // Get asset name before hiding/unhiding for success message
      const assetName = row.assetName || row.originalData?.name || row.originalData?.label || '';
      
      try {
        // Only send publishStatus in payload: Hide (Active→Pending), Unhide (Pending→Active)
        const payload = {
          publishStatus: isHiding ? 'PENDING' : 'ACTIVE',
        };
        
        await this.catalogueApiService.hideOrUnhideItems(assetId, payload);
        console.log(`${isHiding ? 'Hide' : 'Unhide'}: Success - emitting reload event`);
        
        // Optimistically update UI row immediately so the table reflects the change
        row.status = isHiding ? 'Pending' : 'Granted';
        if (row.originalData && typeof row.originalData === 'object') {
          row.originalData.publishStatus = payload.publishStatus;
        }
        // Force change detection for table inputs
        this.contributionsData = [...this.contributionsData];

        // Show success toaster message
        const actionWord = isHiding ? 'hid' : 'unhid';
        const successMessage = assetName
          ? `Successfully ${actionWord} asset ${assetName}`
          : `Successfully ${actionWord} asset`;
        this.toasterService.success(successMessage);
        
        // Emit event to parent to reload data.
        // NOTE: backend can be eventually consistent; immediate reload may return stale status
        // and overwrite the optimistic UI update. Delay slightly to fetch the updated state.
        setTimeout(() => {
          this.reloadData.emit();
          console.log(`${isHiding ? 'Hide' : 'Unhide'}: Reload event emitted (delayed)`);
        }, 800);
      } catch (error) {
        console.error(`Error ${isHiding ? 'hiding' : 'unhiding'} asset:`, error);
        // Error toaster is handled by networkService interceptor
        // Still emit reload event to ensure UI reflects current server state
        this.reloadData.emit();
      }
    } else if (action === 'delete') {
      const assetId = row.originalData?.id;
      if (!assetId) {
        console.error('Asset ID not found');
        return;
      }
      this.closeActionDropdown(); // Close dropdown before showing modal
      this.rowToDelete = row;
      this.showDeleteConfirmModal = true;
      this.cdr.detectChanges(); // Ensure confirmation modal appears immediately (same turn)
    }
  }

  /** Open modify metadata modal: fetch asset by id and show preview-metadata form */
  async openModifyMetadataModal(row: ContributionData): Promise<void> {
    const assetId = row.originalData?.id;
    const assetType = row.originalData?.type;
    if (!assetId) {
      console.error('Asset ID not found for modify metadata');
      this.toasterService.error('Asset ID not found');
      return;
    }
    const routePath = this.getRoutePathFromType(assetType);
    if (routePath !== 'dataset' && routePath !== 'model') {
      this.toasterService.error('Modify metadata is not available for this asset type');
      return;
    }
    this.currentModifyAsset = row;
    this.isLoadingAssetData = true;
    this.modifyAssetData = null;
    this.showModifyModal = true;
    try {
      const response = await this.catalogueApiService.getItemById(assetId);
      let assetData = response;
      if (response?.result) {
        assetData = Array.isArray(response.result) ? response.result[0] : response.result;
      } else if (response?.data) {
        assetData = Array.isArray(response.data) ? response.data[0] : response.data;
      } else if (Array.isArray(response) && response.length > 0) {
        assetData = response[0];
      }
      if (!assetData) {
        throw new Error('No asset data found in response');
      }
      this.modifyAssetData = this.mapAssetDataToPreviewFormat(assetData, row.assetType);
    } catch (error) {
      console.error('Error loading asset data for modification:', error);
      this.toasterService.error('Failed to load asset data. Please try again.');
      this.closeModifyModal();
    } finally {
      this.isLoadingAssetData = false;
      this.cdr.detectChanges();
    }
  }

  closeModifyModal(): void {
    this.showModifyModal = false;
    this.currentModifyAsset = null;
    this.modifyAssetData = null;
    this.isLoadingAssetData = false;
  }

  getAssetTypeForModify(): 'dataset' | 'model' | null {
    if (!this.currentModifyAsset) return null;
    const typeLabel = (this.currentModifyAsset.assetType || '').toLowerCase();
    if (typeLabel === 'model') return 'model';
    if (typeLabel === 'dataset') return 'dataset';
    return null;
  }

  private mapAssetDataToPreviewFormat(apiData: any, assetType: string): any {
    const isModel = assetType.toLowerCase().includes('model') ||
                    (apiData.type && Array.isArray(apiData.type) && apiData.type.some((t: string) => t.includes('AiModel'))) ||
                    (apiData.type && typeof apiData.type === 'string' && apiData.type.includes('AiModel'));
    const accessPolicy = apiData.accessPolicy || apiData.access_policy || 'OPEN';
    const permission = accessPolicy === 'OPEN' ? 'Open' :
                       accessPolicy === 'RESTRICTED' ? 'Restricted' : 'Private';
    let yearRange = apiData.yearRange || apiData.year_range || null;
    if (typeof yearRange === 'string' && yearRange.includes('-')) {
      const parts = yearRange.split('-').map((p: string) => p.trim());
      if (parts.length === 2) {
        yearRange = {
          startYear: parseInt(parts[0]) || null,
          endYear: parseInt(parts[1]) || null
        };
      }
    }
    return {
      name: apiData.name || apiData.label || '',
      title: apiData.name || apiData.label || '',
      permission,
      accessPolicy,
      shortDescription: apiData.shortDescription || apiData.short_description || '',
      description: apiData.description || apiData.longDescription || '',
      longDescription: apiData.description || apiData.longDescription || '',
      license: apiData.license || 'Open Government License, India',
      otherLicense: apiData.otherLicense || apiData.other_license || '',
      resourceServer: apiData.resourceServer || apiData.resource_server || '',
      uploadFrequency: apiData.uploadFrequency || apiData.upload_frequency || 'OneTime',
      yearRange,
      geoCoverage: apiData.geoCoverage || apiData.geo_coverage || '',
      industry: apiData.industry || 'Agriculture',
      datasetType: isModel ? null : (apiData.dataBankType || apiData.datasetType || 'Structured'),
      fileFormat: apiData.fileFormat || apiData.file_format || 'CSV',
      tags: Array.isArray(apiData.tags) ? apiData.tags : (apiData.tags ? [apiData.tags] : []),
      sampleDataUrl: apiData.mediaURL || apiData.media_url || apiData.sampleDataUrl || '',
      dataDescriptors: apiData.dataDescriptor || apiData.data_descriptor || [],
      type: apiData.type || []
    };
  }

  async onModifyFormSubmit(formData: any): Promise<void> {
    if (!this.currentModifyAsset) return;
    const assetId = this.currentModifyAsset.originalData?.id;
    if (!assetId) {
      this.toasterService.error('Unable to update asset: missing asset ID');
      return;
    }
    try {
      const payload = this.buildUpdatePayload(formData, assetId);
      await this.catalogueApiService.updateItem(payload);
      this.toasterService.success('Asset metadata updated successfully');
      this.closeModifyModal();
      setTimeout(() => this.reloadData.emit(), 100);
    } catch (error) {
      console.error('Error updating asset:', error);
      this.toasterService.error('Failed to update asset. Please try again.');
    }
  }

  private buildUpdatePayload(formData: any, assetId: string): any {
    const isModel = this.getAssetTypeForModify() === 'model';
    const originalData = this.currentModifyAsset?.originalData || {};
    const rawType = originalData.type;
    const typeArray = Array.isArray(rawType) ? rawType : (rawType ? [rawType] : []);
    const payload: any = {
      id: assetId,
      type: typeArray.length > 0 ? typeArray : (isModel ? ['adex:AiModel'] : ['adex:DataBank']),
      name: formData.title || originalData.name,
      description: formData.longDescription || formData.description || '',
      shortDescription: formData.shortDescription || '',
      tags: Array.isArray(formData.tags) ? formData.tags : [],
      license: formData.license || '',
      industry: formData.industry || '',
      organizationId: originalData.organizationId,
      organizationType: originalData.organizationType,
      uploadedBy: originalData.uploadedBy,
      organization: originalData.organization,
      department: originalData.department
    };
    if (isModel) {
      payload.modelType = formData.datasetType || originalData.modelType;
      payload.fileFormat = formData.fileFormat || '';
      payload.fileSize = originalData.fileSize || '';
      payload.mediaURL = formData.sampleDataUrl || '';
    } else {
      payload.geoCoverage = formData.geoCoverage || '';
      payload.yearRange = this.formatYearRangeAsString(formData.yearRange);
      payload.resourceServer = this.buildResourceServerArray(formData.resourceServer || '');
      payload.uploadFrequency = formData.uploadFrequency || '';
      payload.dataBankType = formData.datasetType || '';
      payload.fileFormat = formData.fileFormat || '';
      payload.dataDescriptor = this.normalizeDataDescriptorToObject(formData.dataDescriptors);
    }
    const permission = formData.permission || 'Open';
    payload.accessPolicy = permission === 'Open' ? 'OPEN' :
                           permission === 'Restricted' ? 'RESTRICTED' : 'PRIVATE';
    return payload;
  }

  private formatYearRangeAsString(yearRange: any): string | null {
    if (yearRange == null) return null;
    if (typeof yearRange === 'string') return yearRange.trim() || null;
    if (typeof yearRange === 'object' && yearRange.startYear != null && yearRange.endYear != null) {
      return `${yearRange.startYear}-${yearRange.endYear}`;
    }
    return null;
  }

  private normalizeDataDescriptorToObject(dataDescriptors: any): Record<string, any> {
    if (dataDescriptors && typeof dataDescriptors === 'object' && !Array.isArray(dataDescriptors)) {
      return dataDescriptors;
    }
    const list = Array.isArray(dataDescriptors) ? dataDescriptors : [];
    const obj: Record<string, any> = {};
    list.forEach((desc: any) => {
      if (desc && typeof desc === 'object' && desc.attributeName) {
        obj[desc.attributeName] = {
          parameterType: desc.parameterType || '',
          schemaType: desc.schemaType || ''
        };
      }
    });
    return obj;
  }

  private buildResourceServerArray(urlFromDropdown: string): any[] {
    const defaultId = 'fb54d575-a8e2-47d1-ab34-1a89cb907ff1';
    const base = { id: defaultId, queryTypes: ['ATTR'], accessTypes: ['ATTR'], url: urlFromDropdown || '' };
    if (!urlFromDropdown || typeof urlFromDropdown !== 'string') {
      return [{ ...base, name: 'FILE', type: 'file' }];
    }
    const u = urlFromDropdown.toLowerCase();
    if (u.includes('ogc')) return [{ ...base, name: 'OGC', type: 'ogc' }];
    if (u.includes('rs.iudx') || u.includes('ngsi')) return [{ ...base, name: 'NGSI-LD', type: 'ngsi-ld' }];
    if (u.includes('rs-proxy') || u.includes('gateway')) return [{ ...base, name: 'GATEWAY', type: 'gateway' }];
    return [{ ...base, name: 'FILE', type: 'file' }];
  }

  onModifyFormCancel(): void {
    this.closeModifyModal();
  }

  onConfirmDelete(): void {
    if (!this.rowToDelete) return;
    this.performDelete(this.rowToDelete);
  }

  onCancelDelete(): void {
    this.showDeleteConfirmModal = false;
    this.rowToDelete = null;
  }

  private async performDelete(row: ContributionData): Promise<void> {
    const assetId = row.originalData?.id;
    if (!assetId) {
      this.onCancelDelete();
      return;
    }

    this.isDeleting = true;
    const assetName = row.assetName || row.originalData?.name || row.originalData?.label || '';

    try {
      await this.catalogueApiService.deleteItem(assetId);
      const successMessage = assetName
        ? `Successfully deleted an asset ${assetName}`
        : 'Successfully deleted an asset';
      this.toasterService.success(successMessage);
      this.onCancelDelete();
      // Defer reload so modal closes first; avoids document click / focus side effects breaking dropdown
      setTimeout(() => this.reloadData.emit(), 100);
    } catch (error) {
      console.error('Error deleting asset:', error);
      this.onCancelDelete();
      setTimeout(() => this.reloadData.emit(), 100);
    } finally {
      this.isDeleting = false;
      this.cdr.detectChanges();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null &&
        !target.closest('.action-cell-wrapper') &&
        !target.closest('.action-dropdown-overlay')) {
      this.closeActionDropdown();
    }
  }
}

